import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  ApgarComponent,
  ApgarScore,
  CordManagement,
  DeliveryTimeLog,
  FetalPresentation,
  FullDeliveryRecord,
  LacerationDegree,
  MeconiumPresence,
  PlacentaCompleteness,
  VitaminKRoute,
} from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { useProfile } from '../../../../contexts'
import { useTimeLogConfig } from '../../../../hooks/backend/timeLog/useTimeLogConfig'
import { useLaborCharting } from '../context'

// ─── Shared primitives ────────────────────────────────────────────────────────

function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  required = true,
}: {
  label: string
  value: T | undefined
  options: { id: T; text: string }[]
  onChange: (v: T) => void
  required?: boolean
}) {
  return (
    <Flex direction="column" gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        {label}
        {required && (
          <Text as="span" color="red.400" ml={1}>
            *
          </Text>
        )}
      </Text>
      <Flex gap={2} flexWrap="wrap">
        {options.map(opt => (
          <Button
            key={opt.id}
            size="sm"
            fontFamily="Hero-New"
            colorScheme={value === opt.id ? 'pink' : 'gray'}
            variant={value === opt.id ? 'solid' : 'outline'}
            onClick={() => onChange(opt.id)}>
            {opt.text}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}

const NumericField = ({
  label,
  value,
  onChange,
  unit,
  min = 0,
  max = 9999,
  step = 1,
  precision,
  required = true,
}: {
  label: string
  value: number | undefined
  onChange: (v: number | undefined) => void
  unit?: string
  min?: number
  max?: number
  step?: number
  precision?: number
  required?: boolean
}) => (
  <Flex direction="column" gap={1}>
    <Text
      fontFamily="Hero-New"
      fontWeight={600}
      fontSize="sm"
      color="gray.700"
      _dark={{ color: 'gray.200' }}>
      {label}
      {required && (
        <Text as="span" color="red.400" ml={1}>
          *
        </Text>
      )}
    </Text>
    <HStack>
      <NumberInput
        min={min}
        max={max}
        step={step}
        precision={precision}
        value={value ?? ''}
        onChange={(_, n) => onChange(isNaN(n) ? undefined : n)}
        size="sm"
        maxW="140px">
        <NumberInputField fontFamily="Hero-New" />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
      {unit && (
        <Text fontSize="sm" color="gray.500" fontFamily="Hero-New">
          {unit}
        </Text>
      )}
    </HStack>
  </Flex>
)

// ─── Apgar input (reusable — mirrors §4 ApgarInput with a label prop) ──────────

const APGAR_COMPONENTS: { key: keyof Omit<ApgarScore, 'total'>; label: string }[] = [
  { key: 'heartRate', label: 'Heart Rate' },
  { key: 'respirations', label: 'Respirations' },
  { key: 'tone', label: 'Tone' },
  { key: 'reflex', label: 'Reflex' },
  { key: 'color', label: 'Color' },
]
const APGAR_VALUES: ApgarComponent[] = [0, 1, 2]
const EMPTY_APGAR: Omit<ApgarScore, 'total'> = {
  heartRate: undefined as unknown as ApgarComponent,
  respirations: undefined as unknown as ApgarComponent,
  tone: undefined as unknown as ApgarComponent,
  reflex: undefined as unknown as ApgarComponent,
  color: undefined as unknown as ApgarComponent,
}
const computeApgarTotal = (s: Omit<ApgarScore, 'total'>) =>
  (s.heartRate ?? 0) + (s.respirations ?? 0) + (s.tone ?? 0) + (s.reflex ?? 0) + (s.color ?? 0)
const apgarColor = (t: number) => (t >= 7 ? 'green' : t >= 4 ? 'yellow' : 'red')

const ApgarInputBlock = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: Omit<ApgarScore, 'total'>
  onChange: (v: Omit<ApgarScore, 'total'>) => void
}) => {
  const total = computeApgarTotal(value)
  const allSet = APGAR_COMPONENTS.every(c => value[c.key] != null)
  return (
    <Flex direction="column" gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        {label}{' '}
        <Text as="span" color="red.400">
          *
        </Text>
      </Text>
      <Box
        borderRadius={6}
        border="1px solid"
        borderColor="gray.200"
        _dark={{ borderColor: 'whiteAlpha.200' }}
        overflow="hidden">
        {APGAR_COMPONENTS.map(({ key, label: cl }) => (
          <Flex
            key={key}
            align="center"
            px={3}
            py={2}
            borderBottom="1px solid"
            borderColor="gray.100"
            _dark={{ borderBottomColor: 'whiteAlpha.100' }}
            gap={3}>
            <Text
              fontFamily="Hero-New"
              fontSize="sm"
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              flex={1}>
              {cl}
            </Text>
            <Flex gap={1.5}>
              {APGAR_VALUES.map(v => (
                <Button
                  key={v}
                  size="xs"
                  w="32px"
                  fontFamily="Hero-New"
                  fontWeight={700}
                  colorScheme={value[key] === v ? 'pink' : 'gray'}
                  variant={value[key] === v ? 'solid' : 'outline'}
                  onClick={() => onChange({ ...value, [key]: v })}>
                  {v}
                </Button>
              ))}
            </Flex>
          </Flex>
        ))}
        <Flex
          align="center"
          justify="space-between"
          px={3}
          py={2}
          bg="gray.50"
          _dark={{ bg: 'gray.750' }}>
          <Text fontFamily="Hero-New" fontSize="sm" fontWeight={600} color="gray.600">
            Total
          </Text>
          {allSet ? (
            <Badge
              colorScheme={apgarColor(total)}
              fontFamily="Hero-New"
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="full">
              {total} / 10
            </Badge>
          ) : (
            <Text fontFamily="Hero-New" fontSize="sm" color="gray.400">
              —
            </Text>
          )}
        </Flex>
      </Box>
    </Flex>
  )
}

// ─── §5.2 Delivery Time Log ────────────────────────────────────────────────────

const DeliveryTimeLogSection = ({
  value,
  onChange,
  intrapartumTotalMinutes,
  lacerationRepairDone,
}: {
  value: DeliveryTimeLog
  onChange: (v: DeliveryTimeLog) => void
  intrapartumTotalMinutes: number | undefined
  lacerationRepairDone: boolean
}) => {
  const { deliveryTimeLogLabels, deliveryTimeThresholds } = useTimeLogConfig()

  const total = useMemo(() => {
    const base =
      (value.activeManagementMinutes ?? 0) +
      (value.deliveryAttendanceMinutes ?? 0) +
      (value.immediatePostpartumMinutes ?? 0) +
      (value.newbornAssessmentMinutes ?? 0) +
      (value.documentationMinutes ?? 0)
    const repair = lacerationRepairDone ? (value.lacerationRepairMinutes ?? 0) : 0
    return base + repair
  }, [value, lacerationRepairDone])

  const set = (key: keyof DeliveryTimeLog, minutes: number | undefined) =>
    onChange({ ...value, [key]: minutes, totalDeliveryMinutes: total })

  return (
    <Flex direction="column" gap={4}>
      <Text
        fontFamily="Hero-New"
        fontWeight={700}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        Delivery Time Documentation
      </Text>
      <Alert status="info" borderRadius={6} py={2} fontSize="xs">
        <AlertIcon boxSize={3} />
        Time documentation supports complete billing records. All fields are required.
      </Alert>

      {/* Active management — auto-populated from §4.3 intrapartum time log */}
      <Flex direction="column" gap={1}>
        <Text
          fontFamily="Hero-New"
          fontWeight={600}
          fontSize="sm"
          color="gray.700"
          _dark={{ color: 'gray.200' }}>
          {deliveryTimeLogLabels.activeManagementMinutes}
        </Text>
        <HStack>
          <NumberInput
            min={0}
            max={9999}
            value={value.activeManagementMinutes ?? intrapartumTotalMinutes ?? ''}
            onChange={(_, n) => set('activeManagementMinutes', isNaN(n) ? undefined : n)}
            size="sm"
            maxW="120px">
            <NumberInputField fontFamily="Hero-New" />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text fontSize="sm" color="gray.500" fontFamily="Hero-New">
            min
          </Text>
          {intrapartumTotalMinutes != null && value.activeManagementMinutes == null && (
            <Badge colorScheme="blue" fontSize="xs" fontFamily="Hero-New">
              Auto-filled from intrapartum log
            </Badge>
          )}
        </HStack>
      </Flex>

      {/* Other time fields */}
      {(
        [
          { key: 'deliveryAttendanceMinutes' as const, required: true },
          { key: 'immediatePostpartumMinutes' as const, required: true },
          { key: 'newbornAssessmentMinutes' as const, required: true },
          { key: 'documentationMinutes' as const, required: true },
        ] as const
      ).map(({ key, required }) => (
        <Flex key={key} direction="column" gap={1}>
          <Text
            fontFamily="Hero-New"
            fontWeight={600}
            fontSize="sm"
            color="gray.700"
            _dark={{ color: 'gray.200' }}>
            {deliveryTimeLogLabels[key]}
            {required && (
              <Text as="span" color="red.400" ml={1}>
                *
              </Text>
            )}
          </Text>
          <HStack>
            <NumberInput
              min={0}
              max={9999}
              value={value[key] ?? ''}
              onChange={(_, n) => set(key, isNaN(n) ? undefined : n)}
              size="sm"
              maxW="120px">
              <NumberInputField fontFamily="Hero-New" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="sm" color="gray.500" fontFamily="Hero-New">
              min
            </Text>
          </HStack>
        </Flex>
      ))}

      {/* Laceration repair — Tier 1 only if repair performed */}
      {lacerationRepairDone && (
        <Flex direction="column" gap={1}>
          <Text
            fontFamily="Hero-New"
            fontWeight={600}
            fontSize="sm"
            color="gray.700"
            _dark={{ color: 'gray.200' }}>
            {deliveryTimeLogLabels.lacerationRepairMinutes}{' '}
            <Text as="span" color="red.400">
              *
            </Text>
          </Text>
          <HStack>
            <NumberInput
              min={0}
              max={9999}
              value={value.lacerationRepairMinutes ?? ''}
              onChange={(_, n) => set('lacerationRepairMinutes', isNaN(n) ? undefined : n)}
              size="sm"
              maxW="120px">
              <NumberInputField fontFamily="Hero-New" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="sm" color="gray.500" fontFamily="Hero-New">
              min
            </Text>
          </HStack>
        </Flex>
      )}

      {/* Total */}
      <Flex
        direction="column"
        gap={1}
        p={4}
        bg="pink.50"
        borderRadius={8}
        _dark={{ bg: 'pink.900' }}>
        <Text
          fontFamily="Hero-New"
          fontSize="xs"
          fontWeight={600}
          color="pink.600"
          _dark={{ color: 'pink.300' }}>
          TOTAL DELIVERY SERVICE TIME
        </Text>
        <Text
          fontFamily="Hero-New"
          fontSize="2xl"
          fontWeight={700}
          color="pink.700"
          _dark={{ color: 'pink.200' }}>
          {total > 0 ? `${total} min` : '—'}
        </Text>
        <Text fontFamily="Hero-New" fontSize="xs" color="gray.500">
          Used by billing team for AMA delivery code review.
        </Text>
      </Flex>

      {/* §0.3 compliance: AMA code mapping not shown to provider */}
      {deliveryTimeThresholds.length > 0 && (
        <Alert status="info" borderRadius={6} py={2} fontSize="xs">
          <AlertIcon boxSize={3} />
          AMA delivery code suggestions based on total time are available to the billing team only —
          not displayed here.
        </Alert>
      )}
    </Flex>
  )
}

// ─── Section header ───────────────────────────────────────────────────────────

const SectionHeader = ({ label }: { label: string }) => (
  <Text
    fontFamily="Hero-New"
    fontWeight={700}
    fontSize="md"
    color="pink.700"
    _dark={{ color: 'pink.200' }}
    pt={2}>
    {label}
  </Text>
)

// ─── Confirmed value from §4 minimum delivery report ──────────────────────────

const ConfirmedValue = ({ label, value }: { label: string; value: string }) => (
  <Flex align="baseline" gap={2}>
    <Text fontFamily="Hero-New" fontSize="sm" color="gray.500" minW="160px">
      {label}
    </Text>
    <Badge colorScheme="gray" fontFamily="Hero-New" fontSize="sm">
      {value}
    </Badge>
  </Flex>
)

// ─── Main modal ───────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean
  onClose: () => void
}

const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

const formatApgar = (s: ApgarScore | undefined) =>
  s
    ? `${s.total}/10 (HR${s.heartRate} R${s.respirations} T${s.tone} Rf${s.reflex} C${s.color})`
    : '—'

const toDatetimeLocalValue = (ms: number): string => {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export const FullDeliveryRecordModal = ({ isOpen, onClose }: Props) => {
  const { assessmentId } = useProfile()
  const { authUser } = useAuth()
  const {
    minimumDeliveryReport: minReport,
    fullDeliveryRecord: existing,
    saveFullDeliveryRecord,
    intrapartumTimeLog,
  } = useLaborCharting()

  // §5.1 fields
  const [deliveringProviderId] = useState(existing?.deliveringProviderId ?? '')
  const [fetalPresentation, setFetalPresentation] = useState<FetalPresentation | undefined>(
    existing?.fetalPresentation,
  )
  const [cordManagement, setCordManagement] = useState<CordManagement | undefined>(
    existing?.cordManagement,
  )
  const [dccDurationSeconds, setDccDurationSeconds] = useState<number | undefined>(
    existing?.dccDurationSeconds,
  )
  const [meconiumPresence, setMeconiumPresence] = useState<MeconiumPresence | undefined>(
    existing?.meconiumPresence,
  )
  const [eblMl, setEblMl] = useState<number | undefined>(existing?.estimatedBloodLossDeliveryMl)
  const [placentaTime, setPlacentaTime] = useState('')
  const [placentaCompleteness, setPlacentaCompleteness] = useState<
    PlacentaCompleteness | undefined
  >(existing?.placentaCompleteness)
  const [apgar5, setApgar5] = useState<Omit<ApgarScore, 'total'>>(
    existing?.apgar5Minute ? { ...existing.apgar5Minute } : { ...EMPTY_APGAR },
  )
  const [apgar10, setApgar10] = useState<Omit<ApgarScore, 'total'>>(
    existing?.apgar10Minute ? { ...existing.apgar10Minute } : { ...EMPTY_APGAR },
  )
  const [birthWeightGrams, setBirthWeightGrams] = useState<number | undefined>(
    existing?.birthWeightGrams,
  )
  const [skinToSkin, setSkinToSkin] = useState<boolean | undefined>(existing?.skinToSkinInitiated)
  const [skinToSkinTime, setSkinToSkinTime] = useState('')
  const [laceration, setLaceration] = useState<LacerationDegree | undefined>(existing?.laceration)
  const [lacerationRepair, setLacerationRepair] = useState<boolean | undefined>(
    existing?.lacerationRepairPerformed,
  )
  const [eyeProphylaxis, setEyeProphylaxis] = useState(!!existing?.eyeProphylaxisAdministered)
  const [vitaminK, setVitaminK] = useState(!!existing?.vitaminKAdministered)
  const [vitaminKRoute, setVitaminKRoute] = useState<VitaminKRoute | undefined>(
    existing?.vitaminKRoute,
  )
  // Time pickers — string state for datetime-local inputs, converted to ms on save
  const [lacerationRepairTime, setLacerationRepairTime] = useState(
    existing?.lacerationRepairTime ? toDatetimeLocalValue(existing.lacerationRepairTime) : '',
  )
  const [eyeProphylaxisTime, setEyeProphylaxisTime] = useState(
    existing?.eyeProphylaxisDocumentedAt
      ? toDatetimeLocalValue(existing.eyeProphylaxisDocumentedAt)
      : '',
  )
  const [vitaminKTime, setVitaminKTime] = useState(
    existing?.vitaminKAdministeredAt ? toDatetimeLocalValue(existing.vitaminKAdministeredAt) : '',
  )

  // §5.2 time log
  const intrapartumTotalMinutes = intrapartumTimeLog?.totalIntrapartumMinutes
  const [deliveryTimeLog, setDeliveryTimeLog] = useState<DeliveryTimeLog>(
    existing?.deliveryTimeLog ?? {},
  )

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apgar5Complete = APGAR_COMPONENTS.every(c => apgar5[c.key] != null)
  const apgar5Total = apgar5Complete ? computeApgarTotal(apgar5) : 0
  const needApgar10 = apgar5Complete && apgar5Total <= 6
  const apgar10Complete = APGAR_COMPONENTS.every(c => apgar10[c.key] != null)

  const totalEbl = eblMl ?? 0 // intrapartum EBL can be added in future from medication/vitals entries

  const lacerationRepairDone = !!lacerationRepair && laceration != null && laceration !== 'none'

  const timeLogComplete =
    (deliveryTimeLog.activeManagementMinutes ?? intrapartumTotalMinutes) != null &&
    deliveryTimeLog.deliveryAttendanceMinutes != null &&
    deliveryTimeLog.immediatePostpartumMinutes != null &&
    deliveryTimeLog.newbornAssessmentMinutes != null &&
    deliveryTimeLog.documentationMinutes != null &&
    (!lacerationRepairDone || deliveryTimeLog.lacerationRepairMinutes != null)

  const canSave =
    !!fetalPresentation &&
    !!cordManagement &&
    (cordManagement !== 'delayed' || dccDurationSeconds != null) &&
    !!meconiumPresence &&
    eblMl != null &&
    !!placentaCompleteness &&
    apgar5Complete &&
    birthWeightGrams != null &&
    skinToSkin != null &&
    !!laceration &&
    (laceration === 'none' || lacerationRepair != null) &&
    (!needApgar10 || apgar10Complete) &&
    timeLogComplete

  const handleSave = useCallback(async () => {
    if (!assessmentId || !canSave) return
    setSaving(true)
    setError(null)
    try {
      const apgar5Score: ApgarScore = { ...apgar5, total: computeApgarTotal(apgar5) } as ApgarScore
      const apgar10Score: ApgarScore | undefined = needApgar10
        ? ({ ...apgar10, total: computeApgarTotal(apgar10) } as ApgarScore)
        : undefined

      const placentaTs = placentaTime ? new Date(placentaTime).getTime() : undefined
      const skinTs = skinToSkinTime ? new Date(skinToSkinTime).getTime() : undefined
      const lacerationRepairTs = lacerationRepairTime
        ? new Date(lacerationRepairTime).getTime()
        : undefined
      const eyeProphylaxisTs = eyeProphylaxisTime
        ? new Date(eyeProphylaxisTime).getTime()
        : undefined
      const vitaminKTs = vitaminKTime ? new Date(vitaminKTime).getTime() : undefined

      const finalTimeLog: DeliveryTimeLog = {
        ...deliveryTimeLog,
        activeManagementMinutes: deliveryTimeLog.activeManagementMinutes ?? intrapartumTotalMinutes,
        totalDeliveryMinutes:
          (deliveryTimeLog.activeManagementMinutes ?? intrapartumTotalMinutes ?? 0) +
          (deliveryTimeLog.deliveryAttendanceMinutes ?? 0) +
          (lacerationRepairDone ? (deliveryTimeLog.lacerationRepairMinutes ?? 0) : 0) +
          (deliveryTimeLog.immediatePostpartumMinutes ?? 0) +
          (deliveryTimeLog.newbornAssessmentMinutes ?? 0) +
          (deliveryTimeLog.documentationMinutes ?? 0),
        updatedOn: Date.now(),
        updatedBy: authUser?.uid,
      }

      const record: FullDeliveryRecord = {
        deliveringProviderId: deliveringProviderId.trim() || undefined,
        fetalPresentation,
        cordManagement,
        dccDurationSeconds: cordManagement === 'delayed' ? dccDurationSeconds : undefined,
        meconiumPresence,
        estimatedBloodLossDeliveryMl: eblMl,
        totalEblMl: totalEbl,
        placentaDeliveryTime: placentaTs,
        placentaCompleteness,
        apgar5Minute: apgar5Score,
        apgar10Minute: apgar10Score,
        birthWeightGrams,
        skinToSkinInitiated: skinToSkin,
        skinToSkinTime: skinToSkin ? skinTs : undefined,
        laceration,
        lacerationRepairPerformed: laceration !== 'none' ? lacerationRepair : undefined,
        lacerationRepairTime: lacerationRepairDone ? lacerationRepairTs : undefined,
        eyeProphylaxisAdministered: eyeProphylaxis || undefined,
        eyeProphylaxisDocumentedAt: eyeProphylaxis ? eyeProphylaxisTs : undefined,
        vitaminKAdministered: vitaminK || undefined,
        vitaminKAdministeredAt: vitaminK ? vitaminKTs : undefined,
        vitaminKRoute: vitaminK ? vitaminKRoute : undefined,
        deliveryTimeLog: finalTimeLog,
        savedAt: existing?.savedAt ?? Date.now(),
        lastModifiedAt: Date.now(),
      }

      await saveFullDeliveryRecord(record)
      onClose()
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [
    assessmentId,
    canSave,
    apgar5,
    apgar10,
    needApgar10,
    placentaTime,
    skinToSkinTime,
    lacerationRepairTime,
    eyeProphylaxisTime,
    vitaminKTime,
    deliveryTimeLog,
    intrapartumTotalMinutes,
    lacerationRepairDone,
    authUser,
    deliveringProviderId,
    fetalPresentation,
    cordManagement,
    dccDurationSeconds,
    meconiumPresence,
    eblMl,
    totalEbl,
    placentaCompleteness,
    birthWeightGrams,
    skinToSkin,
    laceration,
    lacerationRepair,
    eyeProphylaxis,
    vitaminK,
    vitaminKRoute,
    existing,
    saveFullDeliveryRecord,
    onClose,
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={8} mx={4} maxH="90vh" display="flex" flexDir="column">
        {/* Header */}
        <Flex
          align="center"
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="pink.200"
          bg="pink.50"
          borderTopRadius={8}
          _dark={{ bg: 'pink.900', borderBottomColor: 'pink.700' }}
          shrink={0}>
          <Text
            fontFamily="Hero-New"
            fontWeight={700}
            fontSize="lg"
            color="pink.700"
            _dark={{ color: 'pink.200' }}>
            Full Delivery Record
          </Text>
          {existing?.savedAt && (
            <Badge colorScheme="green" ml={3} fontFamily="Hero-New" fontSize="xs">
              Saved
            </Badge>
          )}
          <CloseButton color="pink.600" _hover={{ bg: 'pink.100' }} ml="auto" onClick={onClose} />
        </Flex>

        <ModalBody p={0} overflowY="auto" flex={1}>
          <Flex direction="column" gap={5} px={6} py={5}>
            {/* §4 confirmed values */}
            <SectionHeader label="Confirmed from Initial Report" />
            <Alert status="info" borderRadius={6} py={2} fontSize="sm">
              <AlertIcon />
              These fields were captured in the minimum delivery report and cannot be changed here.
              Use the amendment workflow if correction is needed.
            </Alert>
            <VStack align="stretch" spacing={1.5}>
              <ConfirmedValue
                label="Time of Birth"
                value={minReport?.timeOfBirth ? formatDateTime(minReport.timeOfBirth) : '—'}
              />
              <ConfirmedValue label="Delivery Type" value={minReport?.deliveryType ?? '—'} />
              <ConfirmedValue label="Baby Sex" value={minReport?.babySex ?? '—'} />
              <ConfirmedValue label="Apgar 1 Minute" value={formatApgar(minReport?.apgar1Minute)} />
              <ConfirmedValue
                label="Resuscitation"
                value={minReport?.resuscitationRequired ? 'Yes' : 'No'}
              />
              <ConfirmedValue
                label="Immediate Concern"
                value={minReport?.immediateConcernFlag ? 'Yes — flagged to billing' : 'No'}
              />
            </VStack>

            <Divider />

            {/* §5.1 additional delivery fields */}
            <SectionHeader label="Delivery Details" />

            <RadioGroup<FetalPresentation>
              label="Fetal Presentation at Delivery"
              value={fetalPresentation}
              onChange={setFetalPresentation}
              options={[
                { id: 'vertex', text: 'Vertex' },
                { id: 'breech', text: 'Breech' },
                { id: 'transverse', text: 'Transverse' },
                { id: 'face', text: 'Face' },
                { id: 'brow', text: 'Brow' },
                { id: 'compound', text: 'Compound' },
              ]}
            />

            <RadioGroup<CordManagement>
              label="Cord Management"
              value={cordManagement}
              onChange={setCordManagement}
              options={[
                { id: 'immediate', text: 'Immediate Clamping' },
                { id: 'delayed', text: 'Delayed Cord Clamping (DCC)' },
              ]}
            />
            {cordManagement === 'delayed' && (
              <NumericField
                label="DCC Duration"
                value={dccDurationSeconds}
                onChange={setDccDurationSeconds}
                unit="seconds"
                min={0}
                max={600}
              />
            )}

            <RadioGroup<MeconiumPresence>
              label="Meconium Presence"
              value={meconiumPresence}
              onChange={setMeconiumPresence}
              options={[
                { id: 'none', text: 'None' },
                { id: 'thin', text: 'Thin' },
                { id: 'thick', text: 'Thick' },
              ]}
            />
            {meconiumPresence === 'thick' && (
              <Alert status="error" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                Thick meconium — neonatal assessment alert. Ensure neonatal evaluation is
                documented.
              </Alert>
            )}

            <NumericField
              label="Estimated Blood Loss at Delivery"
              value={eblMl}
              onChange={setEblMl}
              unit="mL"
              min={0}
              max={5000}
            />
            {totalEbl >= 500 && (
              <Alert status="error" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                Total EBL ≥500 mL — PPH threshold met. Document management and monitoring.
              </Alert>
            )}

            {/* Placenta */}
            <Flex direction="column" gap={1}>
              <Text
                fontFamily="Hero-New"
                fontWeight={600}
                fontSize="sm"
                color="gray.700"
                _dark={{ color: 'gray.200' }}>
                Placenta Delivery Time{' '}
                <Text as="span" color="red.400">
                  *
                </Text>
              </Text>
              <Input
                type="datetime-local"
                value={placentaTime}
                onChange={e => setPlacentaTime(e.target.value)}
                size="sm"
                fontFamily="Hero-New"
                maxW="260px"
              />
            </Flex>

            <RadioGroup<PlacentaCompleteness>
              label="Placenta Completeness"
              value={placentaCompleteness}
              onChange={setPlacentaCompleteness}
              options={[
                { id: 'complete', text: 'Complete' },
                { id: 'incomplete', text: 'Incomplete' },
                { id: 'retained', text: 'Retained' },
              ]}
            />
            {(placentaCompleteness === 'incomplete' || placentaCompleteness === 'retained') && (
              <Alert status="error" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                {placentaCompleteness === 'retained'
                  ? 'Retained placenta'
                  : 'Incomplete placenta delivery'}{' '}
                — clinical intervention required. Document management.
              </Alert>
            )}

            <Divider />
            <SectionHeader label="Neonatal Assessment" />

            {/* Apgar 5-minute */}
            <ApgarInputBlock label="Apgar — 5 Minute" value={apgar5} onChange={setApgar5} />

            {/* Apgar 10-minute — only prompted if 5-min ≤6 */}
            {needApgar10 && (
              <>
                <Alert status="warning" borderRadius={6} py={2} fontSize="sm">
                  <AlertIcon />
                  5-minute Apgar ≤6 — 10-minute Apgar required.
                </Alert>
                <ApgarInputBlock label="Apgar — 10 Minute" value={apgar10} onChange={setApgar10} />
              </>
            )}

            <NumericField
              label="Newborn Birth Weight"
              value={birthWeightGrams}
              onChange={setBirthWeightGrams}
              unit="grams"
              min={200}
              max={7000}
            />
            {birthWeightGrams != null && birthWeightGrams < 2500 && (
              <Alert status="warning" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                Birth weight &lt;2500g — low birth weight. Document neonatal plan.
              </Alert>
            )}
            {birthWeightGrams != null && birthWeightGrams > 4000 && (
              <Alert status="warning" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                Birth weight &gt;4000g — macrosomia. Review for birth injury assessment.
              </Alert>
            )}

            <RadioGroup<'yes' | 'no'>
              label="Skin-to-Skin Initiated"
              value={skinToSkin == null ? undefined : skinToSkin ? 'yes' : 'no'}
              onChange={v => setSkinToSkin(v === 'yes')}
              options={[
                { id: 'yes', text: 'Yes' },
                { id: 'no', text: 'No' },
              ]}
            />
            {skinToSkin && (
              <Flex direction="column" gap={1}>
                <Text
                  fontFamily="Hero-New"
                  fontWeight={600}
                  fontSize="sm"
                  color="gray.700"
                  _dark={{ color: 'gray.200' }}>
                  Skin-to-Skin Time
                </Text>
                <Input
                  type="datetime-local"
                  value={skinToSkinTime}
                  onChange={e => setSkinToSkinTime(e.target.value)}
                  size="sm"
                  fontFamily="Hero-New"
                  maxW="260px"
                />
              </Flex>
            )}

            <Divider />
            <SectionHeader label="Maternal Postpartum" />

            <RadioGroup<LacerationDegree>
              label="Laceration"
              value={laceration}
              onChange={setLaceration}
              options={[
                { id: 'none', text: 'None' },
                { id: 'first', text: '1st Degree' },
                { id: 'second', text: '2nd Degree' },
                { id: 'third', text: '3rd Degree' },
                { id: 'fourth', text: '4th Degree' },
                { id: 'periurethral', text: 'Periurethral' },
              ]}
            />
            {laceration && laceration !== 'none' && (
              <>
                <RadioGroup<'yes' | 'no'>
                  label="Laceration Repair Performed"
                  value={lacerationRepair == null ? undefined : lacerationRepair ? 'yes' : 'no'}
                  onChange={v => {
                    setLacerationRepair(v === 'yes')
                    if (v === 'yes' && !lacerationRepairTime) {
                      setLacerationRepairTime(toDatetimeLocalValue(Date.now()))
                    }
                  }}
                  options={[
                    { id: 'yes', text: 'Yes' },
                    { id: 'no', text: 'No' },
                  ]}
                />
                {lacerationRepair && (
                  <Flex direction="column" gap={1}>
                    <Text
                      fontFamily="Hero-New"
                      fontWeight={600}
                      fontSize="sm"
                      color="gray.700"
                      _dark={{ color: 'gray.200' }}>
                      Time repair performed
                    </Text>
                    <Input
                      type="datetime-local"
                      value={lacerationRepairTime}
                      onChange={e => setLacerationRepairTime(e.target.value)}
                      size="sm"
                      fontFamily="Hero-New"
                      maxW="260px"
                    />
                  </Flex>
                )}
              </>
            )}

            <Flex direction="column" gap={2}>
              <Text
                fontFamily="Hero-New"
                fontWeight={600}
                fontSize="sm"
                color="gray.700"
                _dark={{ color: 'gray.200' }}>
                Newborn Prophylaxis
              </Text>
              <Checkbox
                isChecked={eyeProphylaxis}
                onChange={e => {
                  setEyeProphylaxis(e.target.checked)
                  if (e.target.checked && !eyeProphylaxisTime) {
                    setEyeProphylaxisTime(toDatetimeLocalValue(Date.now()))
                  }
                }}
                colorScheme="pink"
                fontFamily="Hero-New"
                fontSize="sm">
                Antibiotic eye prophylaxis administered
              </Checkbox>
              {eyeProphylaxis && (
                <Flex direction="column" gap={1} ml={6}>
                  <Text
                    fontFamily="Hero-New"
                    fontWeight={600}
                    fontSize="sm"
                    color="gray.700"
                    _dark={{ color: 'gray.200' }}>
                    Time administered
                  </Text>
                  <Input
                    type="datetime-local"
                    value={eyeProphylaxisTime}
                    onChange={e => setEyeProphylaxisTime(e.target.value)}
                    size="sm"
                    fontFamily="Hero-New"
                    maxW="260px"
                  />
                </Flex>
              )}
              <Checkbox
                isChecked={vitaminK}
                onChange={e => {
                  setVitaminK(e.target.checked)
                  if (e.target.checked && !vitaminKTime) {
                    setVitaminKTime(toDatetimeLocalValue(Date.now()))
                  }
                }}
                colorScheme="pink"
                fontFamily="Hero-New"
                fontSize="sm">
                Vitamin K administered
              </Checkbox>
              {vitaminK && (
                <>
                  <RadioGroup<VitaminKRoute>
                    label="Vitamin K Route"
                    value={vitaminKRoute}
                    onChange={setVitaminKRoute}
                    required={false}
                    options={[
                      { id: 'im', text: 'IM' },
                      { id: 'oral', text: 'Oral' },
                    ]}
                  />
                  <Flex direction="column" gap={1} ml={6}>
                    <Text
                      fontFamily="Hero-New"
                      fontWeight={600}
                      fontSize="sm"
                      color="gray.700"
                      _dark={{ color: 'gray.200' }}>
                      Time administered
                    </Text>
                    <Input
                      type="datetime-local"
                      value={vitaminKTime}
                      onChange={e => setVitaminKTime(e.target.value)}
                      size="sm"
                      fontFamily="Hero-New"
                      maxW="260px"
                    />
                  </Flex>
                </>
              )}
            </Flex>

            <Divider />
            <SectionHeader label="§5.2 Delivery Time Documentation" />
            <DeliveryTimeLogSection
              value={deliveryTimeLog}
              onChange={setDeliveryTimeLog}
              intrapartumTotalMinutes={intrapartumTotalMinutes}
              lacerationRepairDone={lacerationRepairDone}
            />

            {error && (
              <Alert status="error" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <HStack justify="flex-end" spacing={3} pb={2}>
              <Button variant="ghost" colorScheme="gray" fontFamily="Hero-New" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="pink"
                fontFamily="Hero-New"
                fontWeight={700}
                isLoading={saving}
                isDisabled={!canSave}
                onClick={handleSave}>
                {existing?.savedAt ? 'Save Changes' : 'Save Delivery Record'}
              </Button>
            </HStack>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
